import PropTypes from 'prop-types'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Accordion, Box, Button, ButtonGroup, Flex, Grid, GridItem, Heading, IconButton, Text, useDisclosure } from '@chakra-ui/react'
import { UiBreadcrumb, UiCard, UiCardHeader, UiCardBody } from 'components/common'
import { MdAdd, MdDeleteOutline, MdOutlineArrowDropDown, MdOutlineArrowRight, MdOutlineEdit } from 'react-icons/md'
import { createProjectTemplateSection, updateProjectTemplateSection, deleteProjectTemplateSection, getProjectTemplateSectionsWithTasks, createProjectTemplateSectionTask, updateProjectTemplateSectionTask, deleteProjectTemplateSectionTask } from 'api'
import { useAppContext } from 'hooks/useAppContext'
import { ModalSection, ModalRemoveSection, ModalSectionType } from './ModalSection'
import { PROJECT_TASK_TEMPLATE_TYPES } from 'lib'
import { ModalRemoveTask, ModalTask } from './ModalTask'

export const ProjectTaskTemplate = ({ type }) => {
  const { createToast } = useAppContext()
  const [isLoading, setIsLoading] = useState(false)
  const [sectionsWithTasks, setSectionsWithTasks] = useState([])

  const disclosureSection = useDisclosure()
  const disclosureRemoveSection = useDisclosure()
  const [selectedSection, setSelectedSection] = useState(null)

  const disclosureTask = useDisclosure()
  const disclosureRemoveTask = useDisclosure()
  const [selectedTask, setSelectedTask] = useState(null)

  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    loadData()
  }, [type])

  const loadData = useCallback(async () => {
    setIsLoading(true)
    try {
      const { rows } = await getProjectTemplateSectionsWithTasks(type)
      setSectionsWithTasks(rows)
      setIsLoading(false)
    } catch (err) {
      createToast('error', 'Error', err.response.data)
      setIsLoading(false)
    }
  }, [type])

  const breadcrumbNav = useMemo(() => {
    return [
      {
        label: 'Task Template',
        isCurrentPage: true
      },
      {
        label: type === PROJECT_TASK_TEMPLATE_TYPES.General ? 'General' : 'Client',
        isCurrentPage: true
      }
    ]
  }, [type])

  const onAddSection = () => {
    disclosureSection.onOpen()
    setSelectedSection({})
  }

  const onEditSection = (id, type, title, sort) => {
    disclosureSection.onOpen()
    setSelectedSection({ id, type, title, sort })
  }

  const onRemoveSection = (id, title) => {
    disclosureRemoveSection.onOpen()
    setSelectedSection({ id, title })
  }

  const onAddTask = (sectionId) => {
    disclosureTask.onOpen()
    setSelectedTask({ sectionId })
  }

  const onEditTask = (sectionId, id, title, assignedUserId, assignedUserName, sort) => {
    disclosureTask.onOpen()
    setSelectedTask({ sectionId, id, title, assignedUserId, assignedUserName, sort })
  }

  const onRemoveTask = (id, title) => {
    disclosureRemoveTask.onOpen()
    setSelectedTask({ id, title })
  }

  const handleAddSection = async (templateType, title) => {
    setIsSubmitting(true)
    try {
      await createProjectTemplateSection({
        type: templateType,
        title
      })
      createToast('success', 'Template section added.')
      setIsSubmitting(false)
      disclosureSection.onClose()
      loadData()
    } catch (err) {
      createToast('error', 'Error', err.response.data)
      setIsSubmitting(false)
    }
  }

  const handleEditSection = async (templateType, id, title, sort) => {
    setIsSubmitting(true)
    try {
      await updateProjectTemplateSection(id, {
        type: templateType,
        title,
        sort
      })
      createToast('success', 'Template section updated.')
      setIsSubmitting(false)
      disclosureSection.onClose()
      setSelectedSection({})
      loadData()
    } catch (err) {
      createToast('error', 'Error', err.response.data)
      setIsSubmitting(false)
    }
  }

  const handleRemoveSection = async (id) => {
    setIsSubmitting(true)
    try {
      await deleteProjectTemplateSection(id)
      createToast('success', 'Template section removed.')
      setIsSubmitting(false)
      disclosureRemoveSection.onClose()
      loadData()
    } catch (err) {
      createToast('error', 'Error', err.response.data)
      setIsSubmitting(false)
    }
  }

  const handleAddTask = async (sectionId, title, assignedUserId) => {
    setIsSubmitting(true)
    try {
      await createProjectTemplateSectionTask({
        sectionId,
        title,
        assignedUserId
      })
      createToast('success', 'Template task added.')
      setIsSubmitting(false)
      disclosureTask.onClose()
      loadData()
    } catch (err) {
      createToast('error', 'Error', err.response.data)
      setIsSubmitting(false)
    }
  }

  const handleEditTask = async (id, sectionId, title, assignedUserId, sort) => {
    setIsSubmitting(true)
    try {
      await updateProjectTemplateSectionTask(id, {
        sectionId,
        title,
        assignedUserId,
        sort
      })
      createToast('success', 'Template task updated.')
      setIsSubmitting(false)
      disclosureTask.onClose()
      setSelectedTask({})
      loadData()
    } catch (err) {
      createToast('error', 'Error', err.response.data)
      setIsSubmitting(false)
    }
  }

  const handleRemoveTask = async (id) => {
    setIsSubmitting(true)
    try {
      await deleteProjectTemplateSectionTask(id)
      createToast('success', 'Template task removed.')
      setIsSubmitting(false)
      disclosureRemoveTask.onClose()
      loadData()
    } catch (err) {
      createToast('error', 'Error', err.response.data)
      setIsSubmitting(false)
    }
  }

  return (
    <>
      <UiBreadcrumb items={breadcrumbNav} />
      <Box>
        <Flex justifyContent="space-between" alignItems="center" my={8}>
          <Heading size="lg">Project Task Template - {type}</Heading>
        </Flex>
        <UiCard isLoading={isLoading} borderRadius={0}>
          <UiCardHeader bg="gray.500" p={4}>
            <Heading color="white" size="sm">Template Information</Heading>
          </UiCardHeader>
          <UiCardBody p={8}>
            <Button
              colorScheme="blue"
              size="sm"
              onClick={onAddSection}
              leftIcon={<MdAdd fontSize="20px" />}
            >
              Add Section
            </Button>
            <Accordion
              index={sectionsWithTasks.map((v, idx) => idx)}
              mt={sectionsWithTasks.length ? 6 : 0}
              allowMultiple
            >
              {sectionsWithTasks.map(section => (
                <ProjectTemplateSectionRow
                  key={section.id}
                  section={section}
                  onEditSection={onEditSection}
                  onRemoveSection={onRemoveSection}
                  onAddTask={onAddTask}
                  onEditTask={onEditTask}
                  onRemoveTask={onRemoveTask}
                />
              ))}
            </Accordion>
          </UiCardBody>
        </UiCard>
      </Box>
      <ModalSection
        templateType={type}
        modalType={!selectedSection?.id ? ModalSectionType.Add : ModalSectionType.Edit}
        section={selectedSection}
        handleAdd={handleAddSection}
        handleEdit={handleEditSection}
        isSubmitting={isSubmitting}
        {...disclosureSection}
      />
      <ModalRemoveSection
        section={selectedSection}
        isSubmitting={isSubmitting}
        handleRemove={handleRemoveSection}
        {...disclosureRemoveSection}
      />
      <ModalTask
        templateType={type}
        modalType={!selectedTask?.id ? ModalSectionType.Add : ModalSectionType.Edit}
        task={selectedTask}
        handleAdd={handleAddTask}
        handleEdit={handleEditTask}
        isSubmitting={isSubmitting}
        {...disclosureTask}
      />
      <ModalRemoveTask
        task={selectedTask}
        isSubmitting={isSubmitting}
        handleRemove={handleRemoveTask}
        {...disclosureRemoveTask}
      />
    </>
  )
}

ProjectTaskTemplate.propTypes = {
  type: PropTypes.oneOf(Object.values(PROJECT_TASK_TEMPLATE_TYPES))
}

const ProjectTemplateSectionRow = ({
  section,
  onEditSection,
  onRemoveSection,
  onAddTask,
  onEditTask,
  onRemoveTask,
  ...rest
}) => {
  const [isExpanded, setIsExpanded] = useState(true)

  return (
    <Box
      position="relative"
      {...rest}
    >
      <Flex
        alignItems="center"
        height="40px"
        gap={2}
        onClick={() => setIsExpanded(!isExpanded)}
        borderBottomWidth={1}
        borderBottomStyle="solid"
        borderBottomColor="gray.200"
        _hover={{ bg: 'gray.100', cursor: 'pointer' }}
      >
        {isExpanded ? (
          <MdOutlineArrowDropDown fontSize="24px" />
        ) : (
          <MdOutlineArrowRight fontSize="24px" />
        )}
        <Text fontSize="sm" fontWeight="bold">{section.title}</Text>
      </Flex>
      <ButtonGroup position="absolute" top={1} right={2}>
        <IconButton
          variant="ghost"
          size="sm"
          colorScheme="gray"
          icon={<MdOutlineEdit fontSize="18px" />}
          onClick={() => onEditSection(section.id, section.type, section.title, section.sort)}
        />
        <IconButton
          variant="ghost"
          size="sm"
          colorScheme="gray"
          icon={<MdDeleteOutline fontSize="18px" />}
          onClick={() => onRemoveSection(section.id, section.title)}
        />
      </ButtonGroup>
      {isExpanded && (
        <Box>
          {section.tasks.map(task => (
            <ProjectTemplateSectionTaskRow
              key={task.id}
              sectionId={section.id}
              task={task}
              onEditTask={onEditTask}
              onRemoveTask={onRemoveTask}
            />
          ))}
          <Button
            ml={4}
            mt={2}
            mb={1}
            size="sm"
            variant="ghost"
            leftIcon={<MdAdd fontSize="18px" />}
            onClick={() => onAddTask(section.id)}
          >
            Add Task
          </Button>
        </Box>
      )}
    </Box>
  )
}

ProjectTemplateSectionRow.propTypes = {
  section: PropTypes.object,
  onEditSection: PropTypes.func,
  onRemoveSection: PropTypes.func,
  onAddTask: PropTypes.func,
  onEditTask: PropTypes.func,
  onRemoveTask: PropTypes.func
}

const ProjectTemplateSectionTaskRow = ({ sectionId, task, onEditTask, onRemoveTask, ...rest }) => {
  return (
    <Grid
      key={task.id}
      templateColumns="repeat(7, 1fr)"
      pl={8}
      pr={4}
      pt={2}
      pb="2px"
      gap={4}
      borderBottomWidth={1}
      borderBottomStyle="solid"
      borderBottomColor="gray.200"
      {...rest}
    >
      <GridItem colSpan={4}>
        <Text fontSize="sm">{task.title}</Text>
      </GridItem>
      <GridItem colSpan={2}>
        <Text fontSize="sm">{task.assignedUserName}</Text>
      </GridItem>
      <GridItem colSpan={1} justifySelf="flex-end">
        <ButtonGroup>
          <IconButton
            variant="ghost"
            size="xs"
            colorScheme="gray"
            icon={<MdOutlineEdit fontSize="16px" />}
            onClick={() => onEditTask(sectionId, task.id, task.title, task.assignedUserId, task.assignedUserName, task.sort)}
          />
          <IconButton
            variant="ghost"
            size="xs"
            colorScheme="gray"
            icon={<MdDeleteOutline fontSize="16px" />}
            onClick={() => onRemoveTask(task.id, task.title)}
          />
        </ButtonGroup>
      </GridItem>
    </Grid>
  )
}

ProjectTemplateSectionTaskRow.propTypes = {
  sectionId: PropTypes.number,
  task: PropTypes.object,
  onEditTask: PropTypes.func,
  onRemoveTask: PropTypes.func
}
